import React, { useRef, useState } from "react";

import { CloudUpload } from "icons";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H6R } from "typography/captions";
import { H4R2, H4SB2 } from "typography/headers";
import { v4 as uuidv4 } from "uuid";

const defaultMaxFileSize = 10 * 1000 * 1000 * 10; //100MB
const defaultFileTypes = [
  ".csv",
  ".doc",
  ".docx",
  ".eml",
  ".gif",
  ".jpg",
  ".jpeg",
  ".msg",
  ".pdf",
  ".png",
  ".txt",
  ".xls",
  ".xlsx",
];

export type Translator = (
  key: string,
  options?: Record<string, string | number | boolean | undefined>
) => string;

type FileSelectProps = {
  onChange?: (file: File) => void;
  disabled?: boolean;
  acceptedFileTypes?: string[];
  maxSize?: number;
  className?: string;
  translate: Translator;
  showFileTypes?: boolean;
};
const FileSelect: React.FC<FileSelectProps> = ({
  onChange,
  disabled,
  maxSize = defaultMaxFileSize,
  acceptedFileTypes = defaultFileTypes,
  className,
  translate,
  showFileTypes = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const id = "file-upload--" + uuidv4();

  const handleFilesDropped = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChanged(e.dataTransfer.files[0]);
    }
  };

  const handleFileChanged = (file: File) => {
    if (!disabled) {
      setErrors([]);
      const fileParts = file.name.split(".");
      const fileExtension = fileParts[fileParts.length - 1] ?? "";
      const fileErrors = [];
      if (file.size > maxSize) {
        fileErrors.push(
          translate("fileTooLarge", {
            maxSize: maxSize / 1000 / 1000,
            namespace: "DOCUMENTS",
            group: "UPLOAD",
          })
        );
      }
      if (
        !acceptedFileTypes.find(
          (ext) => ext === `.${fileExtension.toLowerCase()}`
        )
      ) {
        fileErrors.push(
          translate("fileNotSupported", {
            ext: fileExtension,
            namespace: "DOCUMENTS",
            group: "UPLOAD",
          })
        );
      }
      if (fileErrors.length > 0) {
        setErrors(fileErrors);
      } else {
        onChange && onChange(file);
      }
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.files && e.target.files[0]) {
      handleFileChanged(e.target.files[0]);
      e.currentTarget.value = ""; // fix trying to upload same file multiple times
    }
  };
  return (
    <DropArea
      className={className}
      $disabled={disabled}
      onClick={() => {
        fileInputRef?.current?.click();
      }}
      onDrop={handleFilesDropped}
      onDragOver={(e) => {
        e.preventDefault();
      }}
    >
      <IconContainer $disabled={disabled}>
        <UploadIcon size={18} $disabled={disabled} />
      </IconContainer>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        role="input"
        name="file-upload"
        id={id}
        disabled={disabled}
        onChange={handleFileSelect}
        accept={acceptedFileTypes.join(",")}
      />
      <CTAWrapper>
        <UploadLink $disabled={disabled}>
          {translate("clickToUpload", {
            namespace: "DOCUMENTS",
            group: "UPLOAD",
          })}
        </UploadLink>
        <DNDInstructions>
          {translate("dragAndDrop", {
            namespace: "DOCUMENTS",
            group: "UPLOAD",
          })}
        </DNDInstructions>
      </CTAWrapper>
      {showFileTypes && (
        <H6R>{`${acceptedFileTypes.join(", ")} ${
          acceptedFileTypes.length === 1
            ? translate("fileOnly", {
                namespace: "DOCUMENTS",
                group: "UPLOAD",
              })
            : translate("files", {
                namespace: "DOCUMENTS",
                group: "UPLOAD",
              })
        }`}</H6R>
      )}
      {errors.length > 0 && (
        <ErrorWrapper>
          {errors.map((error) => (
            <li key={error}>
              <Error>{error} </Error>
            </li>
          ))}
        </ErrorWrapper>
      )}
    </DropArea>
  );
};

const HiddenInput = styled.input`
  width: 0;
  visibility: hidden;
  height: 0;
`;

const DropArea = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) => css`
    padding: 16px 24px;
    min-height: 124px;
    background-color: ${$disabled ? color.neutral[100] : color.neutral[100]};
    border: 1px dashed
      ${$disabled ? color.foreground.disabled : color.blue[300]};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transition: background-color 0.5s;

    ${!$disabled &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${color.blue[100]};
      }
    `}
  `}
`;

const UploadIcon = styled(CloudUpload)<{ $disabled?: boolean }>`
  color: ${color.neutral[400]};
  transition: color 0.5s;

  ${DropArea}:hover & {
    ${({ $disabled }) =>
      !$disabled &&
      css`
        color: ${color.blue[300]};
      `}
  }
`;

const IconContainer = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) => css`
    min-width: 40px;
    min-height: 40px;
    background-color: ${color.neutral[200]};
    border-radius: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.5s;

    ${DropArea}:hover & {
      ${!$disabled && `background-color: ${color.blue[100]}`};
    }
  `}
`;

const CTAWrapper = styled.div`
  margin-top: 12px;
`;

const UploadLink = styled(H4SB2)<{ $disabled?: boolean }>`
  ${({ $disabled }) => css`
    color: ${$disabled ? color.foreground.disabled : color.blue[300]};
    display: inline;
  `}
`;

const DNDInstructions = styled(H4R2)`
  display: inline;
  color: ${color.neutral[400]};
  margin-left: 4px;
`;

const ErrorWrapper = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
`;

const Error = styled(H6R)`
  color: ${color.red[300]};
`;

export default FileSelect;
